'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@mokoboko/components';
import { Eye, EyeOff } from 'lucide-react';
import { Modal, Input, Button, Avatar } from '@mokoboko/components';
import { PatternModalHeader } from '@/components/dialogs/pattern-modal-header';
import { resetPassword } from '@/services/auth';
import { PASSWORD_MISMATCH_MESSAGE, passwordField } from '@/lib/schemas';
import { GENERIC_ERROR_MESSAGE } from '@/lib/constants';
import useTenant from '@/state/tenant';

const schema = z
  .object({
    newPassword: passwordField,
    repeatNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.repeatNewPassword, {
    message: PASSWORD_MISMATCH_MESSAGE,
    path: ['repeatNewPassword'],
  });

type FormValues = z.infer<typeof schema>;

interface CreateNewPasswordDialogProps {
  open: boolean;
  onClose: VoidFunction;
  token: string;
}

export const CreateNewPasswordDialog = ({
  open,
  onClose,
  token,
}: CreateNewPasswordDialogProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const { data: tenant } = useTenant();

  const logoUrl = tenant?.logo || '/images/logo.png';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setIsProcessing(true);
      setFormError('');

      await resetPassword({
        token,
        password: data.newPassword,
      });

      toast.success('Your password has been reset successfully');
      reset();
      onClose();
    } catch (error: any) {
      setFormError(
        error?.message || GENERIC_ERROR_MESSAGE
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <Modal.Content size="sm" hasCloseButton isPreventClose={isProcessing}>
        <PatternModalHeader>
          <Avatar
            src={logoUrl}
            name={tenant?.name || 'MB'}
            size="xl"
            className="relative bg-pine-600 text-white"
          />

          <Modal.Title className="relative mt-4 text-gray-900">
            Reset your password
          </Modal.Title>
          <Modal.Description className="relative mt-1 max-w-sm text-gray-700">
            Enter your new password below.
          </Modal.Description>
        </PatternModalHeader>

        <Modal.Body className="flex flex-col gap-6 overflow-visible p-5 pt-0">
          <form className="space-y-5">
            {formError && (
              <p className="text-sm leading-5 text-error-500">{formError}</p>
            )}

            <div className="space-y-4">
              <Input
                {...register('newPassword')}
                type={showPassword ? 'text' : 'password'}
                hasError={!!errors?.newPassword}
                errorMessage={errors?.newPassword?.message}
                placeholder="New password*"
                trailingIcon={showPassword ? EyeOff : Eye}
                onTrailingIconClick={() => setShowPassword((v) => !v)}
                trailingIconAriaLabel="Toggle password visibility"
              />

              <Input
                {...register('repeatNewPassword')}
                type={showRepeatPassword ? 'text' : 'password'}
                hasError={!!errors?.repeatNewPassword}
                errorMessage={errors?.repeatNewPassword?.message}
                placeholder="Repeat new password*"
                trailingIcon={showRepeatPassword ? EyeOff : Eye}
                onTrailingIconClick={() => setShowRepeatPassword((v) => !v)}
                trailingIconAriaLabel="Toggle password visibility"
              />
            </div>

            <Button
              fullWidth
              isProcessing={isProcessing}
              type="button"
              onClick={handleSubmit(onSubmit)}
              size="lg"
            >
              {isProcessing ? 'Resetting password…' : 'Reset password'}
            </Button>
          </form>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
